import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AdSpot, CampaignPackage, CampaignPackageType } from "./orderAd.entity";
import {
  GetDefaultCampaignPackagesResponse,
  SubmitAdOrderRequest,
} from "./orderAd.api";
import { replace } from "redux-first-history";
import { AppPage, AppPageUrls } from "../navigation/navigation.slice";
import ym from "react-yandex-metrika";
import { getUserCustomCampaignPackageThunk } from "./orderAd.thunk";
import { strings } from "intl/fa";
import { PackageInfo } from "components/content/packagesPage/packages";

export enum OrderAdPages {
  SelectAdType,
  DialogMainInfo,
  DialogMoreInfo,
  DialogMoreSettings,
  DialogPreview,
  Invoice,
  AdditionalInfo,
  SubmitMyBankAd,
  SubmitOnboardingAd,
  CampaignMainInfo,
  CampaignMoreInfo,
  CampaignMoreSettings,
  CampaignPreview,
  CampaignSelectAdType,
  SponsoredMessageMainInfo,
  CampaignSponsoredMessageMainInfo,
  SponsoredMessageViewOptions,
  SponsoredMessageImageStep,
  SponsoredFinalStep,
  SponsoredDetails,
  CampaignSponsoredMessageViewOptions,
  // SponsoredMessageMoreinfo,
  SponsoredMessagePreview,
  CampaignSponsoredMessagePreview,
}

export enum AdType {
  click = "click",
  view = "view",
}

export type OrderAdInfo = SubmitAdOrderRequest & {
  imageBlob?: string;
  croppedImageURL?: string;
  imageWidth?: number;
  imageHeight?: number;
  adType?: AdType;
  name?: string;
  stop_value?: number;
  is_instant?: boolean;
};

export interface OrderAdStore {
  page: OrderAdPages;
  history: OrderAdPages[];
  info: OrderAdInfo;
  orderLoading: boolean;
  linkIsForceJoin: boolean;
  customPackageInfo: PackageInfo | undefined;
  defaultPackagesInfo?: GetDefaultCampaignPackagesResponse;
}

export const InitialOrderAdState: OrderAdStore = {
  page: OrderAdPages.SelectAdType,
  history: [],
  info: {
    adType: AdType.view,
    spot: AdSpot.AdSpotDialog,
  },
  orderLoading: false,
  linkIsForceJoin: false,
  customPackageInfo: undefined,
};

const OrderAdSlice = createSlice({
  name: "OrderAdSlice",
  initialState: InitialOrderAdState,
  reducers: {
    setPageWithoutRouting(state, action: PayloadAction<OrderAdPages>) {
      state.page = action.payload;
    },
    setHistory(state, action: PayloadAction<OrderAdPages[]>) {
      state.history = action.payload;
    },
    pushToHistory(state, action: PayloadAction<OrderAdPages>) {
      state.history.push(action.payload);
    },
    goBack(state) {
      if (state.history.length > 1) {
        state.page = state.history[state.history.length - 2];
        state.history.pop();
      }
    },
    setOrderAdInfo(state, action: PayloadAction<OrderAdInfo>) {
      state.info = { ...state.info, ...action.payload };
    },
    setOrderLoading(state, action: PayloadAction<boolean>) {
      state.orderLoading = action.payload;
    },
    setLinkIsForceJoin(state, action: PayloadAction<boolean>) {
      state.linkIsForceJoin = action.payload;
    },
    resetState(state) {
      return { ...InitialOrderAdState, page: state.page };
    },
    // TODO: move this to extraReducers ( we should do some refactors on api call system before )
    setDefaultCampaignPackages(
      state,
      action: PayloadAction<GetDefaultCampaignPackagesResponse | undefined>
    ) {
      state.defaultPackagesInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getUserCustomCampaignPackageThunk.fulfilled,
      (state, action) => {
        if (!action.payload) return;
        const dailyViewCapacity = action.payload.daily_view_capacity! / 1000000;
        const creditExpireDays = action.payload.credit_expire_days!;
        const allowedConcurrentCampaigns =
          action.payload.allowed_concurrent_campaigns!;
        const baseCredit = action.payload?.base_credit!;
        state.customPackageInfo = {
          name: "custom",
          desc: strings.packages.modal.package_item.custom_desc,
          cuncurrent_campaigns: allowedConcurrentCampaigns,
          packageType: CampaignPackageType.CUSTOM,
          daily_view: dailyViewCapacity,
          price_rial: baseCredit,
          discounted_percent: 0,
          credit_expire_period: creditExpireDays,
          view_count: +(dailyViewCapacity * creditExpireDays).toFixed(1),
        };
      }
    );
  },
});

const OrderAdPagesUrls = {
  [OrderAdPages.SelectAdType]: "/select_spot",

  [OrderAdPages.DialogMainInfo]: "/dialog/main_info",
  [OrderAdPages.DialogMoreInfo]: "/dialog/select_type",
  [OrderAdPages.DialogMoreSettings]: "/dialog/more_settings",
  [OrderAdPages.DialogPreview]: "/dialog/preview",

  [OrderAdPages.CampaignMainInfo]: "/campaign/main_info",
  [OrderAdPages.CampaignMoreInfo]: "/campaign/select_type",
  [OrderAdPages.CampaignMoreSettings]: "/campaign/more_settings",
  [OrderAdPages.CampaignPreview]: "/campaign/preview",

  [OrderAdPages.Invoice]: "/invoice",
  [OrderAdPages.AdditionalInfo]: "/more_info",
  [OrderAdPages.SubmitMyBankAd]: "/my_bank",
  [OrderAdPages.SubmitOnboardingAd]: "/onboarding",

  [OrderAdPages.SponsoredMessageMainInfo]: "/sponsored_message/main_info",
  [OrderAdPages.SponsoredMessageViewOptions]: "/sponsored_message/view_options",
  // [OrderAdPages.SponsoredMessageMoreinfo]: "/sponsored_message/more_info",
  [OrderAdPages.SponsoredMessagePreview]: "/sponsored_message/preview",

  [OrderAdPages.SponsoredMessageImageStep]: "/sponsored_message/image",
  [OrderAdPages.SponsoredDetails]: "/sponsored_message/details",

  [OrderAdPages.SponsoredFinalStep]: "/sponsored_message/final_step",

  [OrderAdPages.CampaignSponsoredMessageMainInfo]:
    "/campaign_sponsored_message/main_info",

  [OrderAdPages.CampaignSponsoredMessageViewOptions]:
    "/campaign_sponsored_message/view_options",
  [OrderAdPages.CampaignSponsoredMessagePreview]:
    "/campaign_sponsored_message/preview",
  [OrderAdPages.CampaignSelectAdType]: "/campaign/select_spot",
};

const setPage = (page: OrderAdPages) => {
  return (dispatch: any) => {
    const newUrl = AppPageUrls[AppPage.SubmitAd] + OrderAdPagesUrls[page];
    ym("hit", newUrl);
    dispatch(OrderAdActions.setPageWithoutRouting(page));
    dispatch(OrderAdActions.pushToHistory(page));
    dispatch(replace(newUrl));
  };
};

export const changeBreadcrumb = (history: OrderAdPages[]) => {
  return (dispatch: any) => {
    const page = history[history.length - 1];
    const newUrl = AppPageUrls[AppPage.SubmitAd] + OrderAdPagesUrls[page];
    ym("hit", newUrl);
    dispatch(OrderAdActions.setPageWithoutRouting(page));
    dispatch(OrderAdActions.setHistory(history));
    dispatch(replace(newUrl));
  };
};

export const OrderAdActions = {
  ...OrderAdSlice.actions,
  setPage,
  changeBreadcrumb,
};
export const OrderAdReducer = OrderAdSlice.reducer;
